import { FormattedMessage, useIntl } from 'react-intl';
import {
    Alert,
    AlertContent,
    Button,
    DateField,
    Form,
    Group,
    Label,
    Modal,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalLayout,
    ModalTitle,
    TextField,
    YearAndMonthPickerPopover,
} from '@tlx/atlas';
import { dateUtil } from '@Util/date';
import { SpinnerButton } from '@tlx/tsk-shared';
import { FormEvent } from 'react';
import { SimplifiedPaymentSection } from './SimplifiedPaymentSection';

const formId = 'createSalaryForm';

type Props = {
    handleClose: React.Dispatch<React.SetStateAction<boolean>>;
    period: Date;
    submitForm: (event: FormEvent<HTMLFormElement>) => void;
    awaitingResponse: boolean;
    isHistorical: boolean;
    error?: string;
};

export function CreateSalaryModalStandard({
    handleClose,
    period,
    submitForm,
    awaitingResponse,
    isHistorical,
    error,
}: Props) {
    const intl = useIntl();
    return (
        <Modal
            open={true}
            onClose={() => {
                if (!awaitingResponse) {
                    // to avoid user "cancelling" the dialog by clicking outside it
                    handleClose(false);
                }
            }}
            className={'tsk-width-500'}
        >
            <ModalLayout>
                {!awaitingResponse && <ModalCloseButton />}
                <ModalTitle>
                    <FormattedMessage
                        id={isHistorical ? 'text_new_historical_wage_transaction' : 'tsk_text_create_salary_payment'}
                    />
                </ModalTitle>
                <ModalContent>
                    {error && (
                        <Alert variant="error" className={'atl-mb-24'}>
                            <AlertContent>{error}</AlertContent>
                        </Alert>
                    )}
                    {isHistorical && (
                        <Alert variant="info" className={'atl-mb-24'}>
                            <AlertContent>
                                <FormattedMessage id={'text_historical_wage_transactions_text'} />
                            </AlertContent>
                        </Alert>
                    )}
                    <Form onSubmit={submitForm} id={formId}>
                        <div className={'atl-flex atl-gap-x-16 atl-mb-24'}>
                            <Group className={'tsk-flex-half'}>
                                <Label>
                                    <FormattedMessage id={'text_period'} />
                                </Label>
                                <YearAndMonthPickerPopover
                                    selectedDate={period}
                                    futureYears={1}
                                    pastYears={20}
                                    disabled
                                />
                            </Group>
                            <DateField
                                className={'tsk-flex-half'}
                                label={intl.formatMessage({
                                    id: 'text_payment_out_date',
                                })}
                                data-testid={'date-selector'}
                                name={'date'}
                                isDisabled={awaitingResponse}
                                defaultValue={dateUtil.formatDate(
                                    isHistorical ? dateUtil.getLastDayOfMonth(period) : dateUtil.today(),
                                    'en',
                                )}
                                isRequired
                                validate={(value) => {
                                    const invalidDateFormat = dateUtil.validateDateString(value, intl);
                                    if (invalidDateFormat) {
                                        return invalidDateFormat;
                                    }
                                    if (dateUtil.getYear(value) - dateUtil.getYear(dateUtil.today()) > 20) {
                                        return intl.formatMessage({
                                            id: 'api_illegal_week_year',
                                        });
                                    }
                                }}
                            />
                        </div>
                        <TextField
                            label={intl.formatMessage({
                                id: 'text_description',
                            })}
                            className={'atl-w-full'}
                            data-testid={'description-input'}
                            name={'description'}
                            defaultValue={''}
                            isDisabled={awaitingResponse}
                        />
                        <SimplifiedPaymentSection disabled={awaitingResponse} />
                    </Form>
                </ModalContent>
                <ModalFooter className={'atl-flex atl-gap-8'}>
                    <SpinnerButton
                        className="atl-button--primary"
                        data-testid="create-salary-run-button"
                        type={'submit'}
                        form={formId}
                        disabled={awaitingResponse}
                        isLoading={awaitingResponse}
                    >
                        <FormattedMessage id={'button_create'} />
                    </SpinnerButton>
                    <Button
                        variant={'secondary'}
                        onClick={() => {
                            handleClose(false);
                        }}
                        data-testid="create-salary-modal-cancel-button"
                        disabled={awaitingResponse}
                    >
                        <FormattedMessage id={'text_cancel'} />
                    </Button>
                </ModalFooter>
            </ModalLayout>
        </Modal>
    );
}
